// frontend/src/pages/doctor/ConsultationDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, User, Calendar, Clock, Phone, Mail, 
  FileText, Pill, Activity, AlertCircle, CheckCircle,
  XCircle, Video
} from 'lucide-react';
import doctorService from '../../services/doctorService';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const ConsultationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [completionData, setCompletionData] = useState({
    diagnosis: '',
    prescription: [],
    recommendations: '',
    followUpDate: '',
    doctorNotes: ''
  });
  const [medicationInput, setMedicationInput] = useState('');

  useEffect(() => {
    fetchConsultationDetail();
  }, [id]);

  const fetchConsultationDetail = async () => {
    try {
      setLoading(true);
      const response = await apiService.consultations.getById(id);

      if (response.success) {
        setConsultation(response.data);
        
        if (response.data.preferredTime) {
          setScheduledTime(new Date(response.data.preferredTime).toISOString().slice(0, 16));
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch consultation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptConsultation = async () => {
    if (!scheduledTime) {
      alert('Please select a scheduled time');
      return;
    }

    try {
      setActionLoading(true);
      const response = await doctorService.acceptConsultation(id, scheduledTime);

      if (response.success) {
        alert('Consultation accepted successfully!');
        await fetchConsultationDetail();
        setShowAcceptModal(false);
      }
    } catch (err) {
      alert(err.message || 'Failed to accept consultation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConsultation = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      setActionLoading(true);
      const response = await doctorService.rejectConsultation(id, rejectReason);

      if (response.success) {
        alert('Consultation rejected');
        await fetchConsultationDetail();
        setShowRejectModal(false);
      }
    } catch (err) {
      alert(err.message || 'Failed to reject consultation');
    } finally {
      setActionLoading(false);
    }
  };

  const addMedication = () => {
    if (medicationInput.trim()) {
      setCompletionData(prev => ({
        ...prev,
        prescription: [...prev.prescription, medicationInput.trim()]
      }));
      setMedicationInput('');
    }
  };

  const removeMedication = (index) => {
    setCompletionData(prev => ({
      ...prev,
      prescription: prev.prescription.filter((_, i) => i !== index)
    }));
  };

  const handleCompleteConsultation = async () => {
    if (!completionData.diagnosis.trim()) {
      alert('Diagnosis is required');
      return;
    }

    try {
      setActionLoading(true);
      const response = await doctorService.completeConsultation(id, completionData);

      if (response.success) {
        alert('Consultation completed successfully!');
        await fetchConsultationDetail();
        setShowCompleteModal(false);
      }
    } catch (err) {
      alert(err.message || 'Failed to complete consultation');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-700', 
        icon: Clock,
        label: 'Pending' 
      },
      accepted: { 
        bg: 'bg-blue-100', 
        text: 'text-blue-700', 
        icon: CheckCircle,
        label: 'Accepted' 
      },
      completed: { 
        bg: 'bg-green-100', 
        text: 'text-green-700', 
        icon: CheckCircle,
        label: 'Completed' 
      },
      rejected: { 
        bg: 'bg-red-100', 
        text: 'text-red-700', 
        icon: XCircle,
        label: 'Rejected' 
      },
      cancelled: { 
        bg: 'bg-gray-100', 
        text: 'text-gray-700', 
        icon: XCircle,
        label: 'Cancelled' 
      }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-4 h-4" />
        <span>{badge.label}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/doctor/consultations')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Consultations
        </button>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error || 'Consultation not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/doctor/consultations')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Consultation Details</h1>
            <p className="text-gray-600 mt-1">Review consultation information</p>
          </div>
        </div>
        {getStatusBadge(consultation.status)}
      </div>

      {consultation.status === 'pending' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-900">Action Required</p>
                <p className="text-sm text-yellow-700 mt-1">
                  This consultation is awaiting your response
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowAcceptModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Accept
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {consultation.status === 'accepted' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Consultation Scheduled</p>
                <p className="text-sm text-blue-700 mt-1">
                  Scheduled for {new Date(consultation.scheduledAt).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Link
                to={`/doctor/consultation/${consultation._id}`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center"
              >
                <Video className="w-4 h-4 mr-2" />
                Start Consultation
              </Link>
              <button
                onClick={() => setShowCompleteModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Mark Complete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-2 mb-4">
              <User className="w-5 h-5 text-primary-600" />
              <h2 className="text-xl font-bold text-gray-900">Patient Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Full Name</p>
                <p className="text-base font-medium text-gray-900">
                  {consultation.patient?.name || 'Anonymous'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone Number</p>
                <p className="text-base font-medium text-gray-900 flex items-center">
                  <Phone className="w-4 h-4 mr-1 text-gray-500" />
                  {consultation.patient?.phone || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="text-base font-medium text-gray-900 flex items-center">
                  <Mail className="w-4 h-4 mr-1 text-gray-500" />
                  {consultation.patient?.email || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Age</p>
                <p className="text-base font-medium text-gray-900">
                  {consultation.patient?.age || 'N/A'}
                </p>
              </div>
              {consultation.patient?.bloodGroup && (
                <div>
                  <p className="text-sm text-gray-600">Blood Group</p>
                  <p className="text-base font-medium text-gray-900">
                    {consultation.patient.bloodGroup}
                  </p>
                </div>
              )}
              {consultation.patient?.gender && (
                <div>
                  <p className="text-sm text-gray-600">Gender</p>
                  <p className="text-base font-medium text-gray-900 capitalize">
                    {consultation.patient.gender}
                  </p>
                </div>
              )}
            </div>

            {consultation.patient?.medicalHistory && consultation.patient.medicalHistory.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-900 mb-2">Medical History</p>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {consultation.patient.medicalHistory.map((condition, index) => (
                    <li key={index}>{condition}</li>
                  ))}
                </ul>
              </div>
            )}

            {consultation.patient?.allergies && consultation.patient.allergies.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-red-700 mb-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Allergies
                </p>
                <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                  {consultation.patient.allergies.map((allergy, index) => (
                    <li key={index}>{allergy}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="w-5 h-5 text-primary-600" />
              <h2 className="text-xl font-bold text-gray-900">Consultation Details</h2>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Consultation Type</p>
                <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                  consultation.type === 'online'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {consultation.type === 'online' ? 'Online' : 'Physical'}
                </span>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">Symptoms</p>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                  {consultation.symptoms}
                </p>
              </div>

              {consultation.preferredTime && (
                <div>
                  <p className="text-sm font-medium text-gray-600">Preferred Time</p>
                  <p className="text-sm text-gray-900 mt-1 flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-gray-500" />
                    {new Date(consultation.preferredTime).toLocaleString()}
                  </p>
                </div>
              )}

              {consultation.scheduledAt && (
                <div>
                  <p className="text-sm font-medium text-gray-600">Scheduled Time</p>
                  <p className="text-sm text-gray-900 mt-1 flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-gray-500" />
                    {new Date(consultation.scheduledAt).toLocaleString()}
                  </p>
                </div>
              )}

              {consultation.diagnosis && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Diagnosis</p>
                  <p className="text-sm text-gray-900 bg-green-50 p-3 rounded">
                    {consultation.diagnosis}
                  </p>
                </div>
              )}

              {consultation.prescription && consultation.prescription.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Prescription</p>
                  <ul className="space-y-2">
                    {consultation.prescription.map((med, index) => (
                      <li key={index} className="flex items-start bg-blue-50 p-3 rounded">
                        <Pill className="w-4 h-4 mr-2 text-blue-600 mt-0.5" />
                        <span className="text-sm text-gray-900">{med}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {consultation.recommendations && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Recommendations</p>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                    {consultation.recommendations}
                  </p>
                </div>
              )}

              {consultation.followUpDate && (
                <div>
                  <p className="text-sm font-medium text-gray-600">Follow-up Date</p>
                  <p className="text-sm text-gray-900 mt-1 flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-gray-500" />
                    {new Date(consultation.followUpDate).toLocaleDateString()}
                  </p>
                </div>
              )}

              {consultation.doctorNotes && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Doctor's Notes</p>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                    {consultation.doctorNotes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Consultation Fee</h3>
            <div className="text-center py-4 bg-primary-50 rounded-lg">
              <p className="text-3xl font-bold text-primary-600">
                KES {consultation.fee.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Timeline</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Requested</p>
                  <p className="text-xs text-gray-600">
                    {new Date(consultation.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {consultation.acceptedAt && (
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Accepted</p>
                    <p className="text-xs text-gray-600">
                      {new Date(consultation.acceptedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {consultation.completedAt && (
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Activity className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Completed</p>
                    <p className="text-xs text-gray-600">
                      {new Date(consultation.completedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {consultation.rejectedAt && (
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Rejected</p>
                    <p className="text-xs text-gray-600">
                      {new Date(consultation.rejectedAt).toLocaleString()}
                    </p>
                    {consultation.rejectionReason && (
                      <p className="text-xs text-red-600 mt-1">
                        Reason: {consultation.rejectionReason}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <Link
                to="/doctor/patients"
                className="block w-full px-4 py-2 text-center bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                View All Patients
              </Link>
              <Link
                to="/doctor/consultations"
                className="block w-full px-4 py-2 text-center bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                View All Consultations
              </Link>
            </div>
          </div>
        </div>
      </div>

      {showAcceptModal && (
        <Modal
          isOpen={showAcceptModal}
          onClose={() => setShowAcceptModal(false)}
          title="Accept Consultation"
        >
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Patient Information</h4>
              <p className="text-sm text-gray-600">
                <strong>Name:</strong> {consultation.patient?.name || 'Anonymous'}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Symptoms:</strong> {consultation.symptoms}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Consultation Time *
              </label>
              <input
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Doctor's Notes (Private)
              </label>
              <textarea
                value={completionData.doctorNotes}
                onChange={(e) => setCompletionData({ ...completionData, doctorNotes: e.target.value })}
                rows="3"
                placeholder="Private notes for your records..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowCompleteModal(false)}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteConsultation}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Completing...' : 'Complete Consultation'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ConsultationDetail;